import {
  Box,
  Flex,
  Heading,
  Icon,
  Image,
  Link,
  Show,
  Skeleton,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from "@chakra-ui/react";
import PageLayout from "@components/PageLayout";
import type { NextPage } from "next";
import { GoArrowUpRight } from "react-icons/go";
import { IoIosArrowForward } from "react-icons/io";

import { FaBolt } from "react-icons/fa6";
import { MdDiamond } from "react-icons/md";

import { getUrlForLocalImage } from "utils";
import useVaultOverview from "@hooks/useVaultOverview";

import UTMLink from "@components/UTMLink";
import useVaultcraftData from "@hooks/useVaultcraftData";
import useYieldVaultData from "@hooks/useYieldVaultData";
import BaseImage from "@components/BaseImage";
import { Fragment } from "react";

const Landing: NextPage = () => {
  const { isLoadingAPYData, formattedApy, formattedTvl, isLoadingTotalStaked } =
    useVaultOverview();

  const {
    isLoadingAPYData: isLoadingYieldAPYData,
    formattedApy: yieldAPY,
    formattedTvl: yieldTotalStaked,
    isLoadingTotalStaked: isLoadingYieldTotalStaked,
  } = useYieldVaultData();

  const {
    isLoading: grizzlyGainLoading,
    formattedApy: grizzlyGainApy,
    formattedTvl: grizzlyGainTvl,
  } = useVaultcraftData();

  const VAULT_LISTING = [
    {
      key: "yield",
      title: "High Growth Vault",
      icon: "/assets/vaults/hgeth.svg",
      description:
        "Deposit ETH/rsETH/LSTs, earn top rewards, get hgETH, and boost returns with DeFi!",

      rewards: "High APY",
      link: "/gain/growth-vault",
      tags: [
        {
          label: "Estimated Apy",
          value: yieldAPY,
          isLoading: isLoadingYieldAPYData,
        },
        {
          label: "TVL",
          value: yieldTotalStaked,
          isLoading: isLoadingYieldTotalStaked,
        },
      ],
      renderTag: () => (
        <Tag
          bg="#E14B2533"
          borderRadius="full"
          color="#CD5613 "
          width={{ base: "20px", md: "auto" }}
          height={{ base: "20px", md: "auto" }}
          justifyContent="center"
        >
          <TagLeftIcon
            boxSize="16px"
            mr={{ base: "0", md: "4px" }}
            as={FaBolt}
          />
          <Show above="md">
            <TagLabel>Top strategy</TagLabel>
          </Show>
        </Tag>
      ),
    },
    {
      key: "airdrop",
      title: "Airdrop Gain",
      icon: "/assets/agETH.png",

      rewards: "Airdrops + APY",
      description:
        "Deposit ETH/rsETH/LSTs, farm airdrops, earn rewards, receive agETH, and boost returns with DeFi!",

      link: "/gain/airdrop-gain",
      tags: [
        {
          label: "Estimated Apy",
          value: formattedApy,
          isLoading: isLoadingAPYData,
        },
        {
          label: "TVL",
          value: formattedTvl,
          isLoading: isLoadingTotalStaked,
        },
      ],
      renderTag: () => (
        <Tag
          bg="#2196F333"
          borderRadius="full"
          color="#026BBE"
          width={{ base: "20px", md: "auto" }}
          height={{ base: "20px", md: "auto" }}
          justifyContent="center"
        >
          <TagLeftIcon
            mr={{ base: "0", md: "4px" }}
            as={MdDiamond}
            boxSize="16px"
          />
          <Show above="md">
            <TagLabel> Exclusive Airdrops</TagLabel>
          </Show>
        </Tag>
      ),
    },
    {
      key: "grizzly-gain",
      title: "Grizzly Gain",
      icon: "/assets/vaults/ggETH.svg",
      rewards: "Airdrop + APY",
      description:
        "Join the Boyco pre-deposit campaign for Berachain to unlock Bera rewards, & restaking rewards. Earn yield via Compound, Yearn, & Morpho before launch.",

      link: "https://app.vaultcraft.io/vaults/0x11eAA7a46afE1023f47040691071e174125366C8?chainId=1",
      isExternal: true,
      note: () => (
        <Flex
          bg={"#BEC59366"}
          p={3}
          borderRadius={"10px"}
          gap="5px"
          alignItems={"center"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          cursor={"default"}
        >
          <Flex alignItems={"start"}>
            <BaseImage
              src="/assets/info-tips.svg"
              alt=""
              height={24}
              width={24}
            />
          </Flex>
          <Flex>
            <Text fontSize={"12px"} fontWeight={400} color={"#505D01"}>
              The vault is being upgraded. Assets deposited in the vault before
              24th Jan 2025 should be withdrawn from the{" "}
              <Link
                isExternal
                href="https://app.vaultcraft.io/vaults/0xcF9273BA04b875F94E4A9D8914bbD6b3C1f08EDb?chainId=1"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <u>old vault</u>
              </Link>{" "}
              and migrated to the new vault.
            </Text>
          </Flex>
        </Flex>
      ),
      tags: [
        {
          label: "Estimated Apy",
          value: grizzlyGainApy,
          isLoading: grizzlyGainLoading,
        },
        {
          label: "TVL",
          value: grizzlyGainTvl,
          isLoading: grizzlyGainLoading,
        },
      ],
      renderTag: () => (
        <Flex gap="4px" alignItems="center">
          <Show above="md">
            <Text
              fontWeight={400}
              fontSize="12px"
              lineHeight="12px"
              color="#3F6870"
              mt="2px"
            >
              Powered by
            </Text>
          </Show>
          <Tag
            bg="#BEC59366"
            borderRadius="full"
            color="#505D01"
            width={{ base: "20px", md: "auto" }}
            height={{ base: "20px", md: "auto" }}
            justifyContent="center"
          >
            <Image
              src={getUrlForLocalImage("/assets/platforms/vault.svg")}
              alt="Grizzly Gain"
              width="16px"
              height="16px"
              mr={{ base: "0", md: "4px" }}
              borderRadius="50%"
              maxWidth="none"
            />

            <Show above="md">
              <TagLabel>Vault craft</TagLabel>
              <Icon as={GoArrowUpRight} ml="2px" fontSize="14px" mt="2px" />
            </Show>
          </Tag>
        </Flex>
      ),
    },
  ];

  return (
    <PageLayout>
      <Box margin="auto" maxW="1008px" width="90%">
        <Text
          fontWeight={400}
          fontSize={{ base: "14px", md: "20px" }}
          lineHeight={{ base: "16.8px", md: "24px" }}
          color="#3F6870"
          pb="20px"
          pt={{ base: "20px", md: "0" }}
        >
          Gain is your reward concierge! Access top airdrops & high rewards with
          our curated vaults.
        </Text>

        <Flex
          bg="#EBF9F9"
          // padding="10px 0"
          borderRadius="10px"
          direction="column"
        >
          {VAULT_LISTING.map((vault, index) => (
            <Fragment key={vault.key}>
              <UTMLink
                href={vault.link}
                key={vault.key}
                isExternal={vault.isExternal || false}
              >
                <Box
                  padding={{ base: "0 16px", md: "0 40px" }}
                  cursor="pointer"
                  className="group"
                  _hover={{
                    border: { md: "1px solid #B6DFDF" },
                    borderRadius: { md: "10px" },
                    boxShadow: {
                      md: "-4px 0 6px rgba(7, 90, 90, 0.14),4px 0 6px rgba(7, 90, 90, 0.14),0 4px 6px rgba(7, 90, 90, 0.14)",
                    },
                    // clipPath: { md: "inset(-6px 0 0 -6px)" },
                    marginTop: { md: "-1px" },
                  }}
                >
                  <Flex
                    direction={{ base: "column", md: "row" }}
                    borderBottom={
                      index < VAULT_LISTING.length - 1
                        ? "1px solid #B6DFDF"
                        : "none"
                    }
                    _groupHover={{ borderBottom: 0 }}
                    padding={{ base: "30px 0", md: 0 }}
                  >
                    <Flex
                      padding={{ base: 0, md: "30px 0" }}
                      gap="10px"
                      flex={1}
                    >
                      <Show above="md">
                        <Image
                          src={getUrlForLocalImage(vault.icon)}
                          height={{ base: "32px" }}
                          width={{ base: "32px" }}
                          alt={vault.key}
                          mt="2px"
                        />
                      </Show>

                      <Flex
                        direction="column"
                        gap="0.5rem"
                        maxWidth={{ base: "auto", md: "85%" }}
                      >
                        <Flex gap="0.5rem" alignItems="center">
                          <Show below="md">
                            <Image
                              src={getUrlForLocalImage(vault.icon)}
                              height={{ base: "26px" }}
                              width={{ base: "26px" }}
                              alt={vault.key}
                              mt="2px"
                            />
                          </Show>
                          <Heading
                            fontWeight={600}
                            fontSize={{ base: "20px", md: "20px" }}
                            lineHeight={{ base: "24px", md: "24px" }}
                            color="textPrimary"
                          >
                            {vault.title}
                          </Heading>
                          {vault.renderTag && vault.renderTag()}

                          <Show below="md">
                            <Icon
                              as={IoIosArrowForward}
                              color="#3C4852"
                              ml="auto"
                              alignItems="center"
                            />
                          </Show>
                        </Flex>
                        <Text
                          fontSize={{ base: "12px", md: "14px" }}
                          fontWeight={400}
                          color="textSecondary"
                          lineHeight="16.8px"
                        >
                          {vault.description}
                        </Text>

                        <Flex alignItems="center" gap="4px" mt="0.5rem">
                          <Show above="md">
                            <Text
                              fontSize="12px"
                              fontWeight={400}
                              lineHeight="14.4px"
                            >
                              Rewards Type:
                            </Text>
                            <Text
                              fontSize="16px"
                              fontWeight={500}
                              lineHeight="24px"
                            >
                              {vault.rewards}
                            </Text>
                          </Show>
                        </Flex>
                        <Show above="md">{vault.note && vault.note()}</Show>
                      </Flex>
                    </Flex>
                    <Flex alignItems="center" gap="2.5rem">
                      <Show below="md">
                        <Box width="125px">
                          <Text
                            fontSize="10px"
                            fontWeight={400}
                            lineHeight="12px"
                            color="#3F6870"
                            mb="5px"
                          >
                            Reward type
                          </Text>
                          <Text
                            fontSize="14px"
                            fontWeight={500}
                            lineHeight="21px"
                            color="#075A5A"
                          >
                            {vault.rewards}
                          </Text>
                        </Box>
                      </Show>

                      {vault.tags.map((data) => (
                        <Box width="90px" key={data.label}>
                          <Text
                            fontWeight={400}
                            fontSize={{ base: "10px", md: "12px" }}
                            lineHeight={{ base: "12px", md: "14.4px" }}
                            color="#3F6870"
                            mb="5px"
                          >
                            {data.label}
                          </Text>
                          {data.isLoading ? (
                            <Skeleton height="14px" width="50px" />
                          ) : (
                            <Text
                              fontWeight={600}
                              fontSize={{ base: "14px", md: "18px" }}
                              lineHeight="21.6px"
                              color="#259F76"
                            >
                              {data.value}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </Flex>
                    <Show above="md">
                      <Flex alignItems="center">
                        <Icon
                          as={IoIosArrowForward}
                          color="#3C4852"
                          visibility="hidden"
                          _groupHover={{ visibility: "visible" }}
                        />
                      </Flex>
                    </Show>
                  </Flex>

                  <Show below="md">
                    <Box mb="20px">{vault.note && vault.note()}</Box>
                  </Show>
                </Box>
              </UTMLink>
            </Fragment>
          ))}
        </Flex>
      </Box>
    </PageLayout>
  );
};

export default Landing;
